import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getPatternById } from '../../data';
import { Pattern } from '../../types';
import { PatternCard } from '../../components/PatternCard/PatternCard';
import { useReveal } from '../../hooks/useReveal';
import styles from './Quiz.module.css';

interface QuizOption {
  label: string;
  tag: string;
}

interface QuizQuestion {
  key: 'interface' | 'challenge' | 'complexity' | 'concern';
  shortLabel: string;
  question: string;
  options: QuizOption[];
}

const QUESTIONS: QuizQuestion[] = [
  {
    key: 'interface',
    shortLabel: 'Interface type',
    question: 'What type of interface are you designing?',
    options: [
      { label: 'Chat or text-based', tag: 'chat' },
      { label: 'Voice or audio', tag: 'voice' },
      { label: 'Background agent / automated workflow', tag: 'agentic' },
      { label: 'Not sure yet', tag: 'all' },
    ],
  },
  {
    key: 'challenge',
    shortLabel: 'Main challenge',
    question: "What's the main challenge you're facing?",
    options: [
      { label: "Users don't know what the agent is doing", tag: 'transparency' },
      { label: 'Users need to stay in control of agent actions', tag: 'control' },
      { label: 'Something goes wrong and users get stuck', tag: 'error' },
      { label: 'Setting up the agent for first use', tag: 'onboarding' },
      { label: 'The agent needs to communicate with users', tag: 'conversation' },
      { label: "Users don't trust the agent's answers or recommendations", tag: 'sources' },
      { label: "Users don't know what the agent remembers about them", tag: 'memory' },
    ],
  },
  {
    key: 'complexity',
    shortLabel: 'Consequentiality',
    question: "How consequential are the agent's actions?",
    options: [
      { label: 'Low — easy to undo, no real harm if wrong', tag: 'low' },
      { label: 'Medium — takes some effort to fix mistakes', tag: 'medium' },
      { label: "High — affects real people or can't be undone", tag: 'high' },
    ],
  },
  {
    key: 'concern',
    shortLabel: 'Biggest concern',
    question: "What's your biggest concern?",
    options: [
      { label: "Trust — users don't trust the AI", tag: 'trust' },
      { label: "Clarity — users don't understand the AI", tag: 'clarity' },
      { label: "Control — users feel they've lost control", tag: 'control' },
      { label: 'Efficiency — the AI slows users down', tag: 'efficiency' },
    ],
  },
];

type AnswerKey = 'interface' | 'challenge' | 'complexity' | 'concern';
type Answers = Partial<Record<AnswerKey, string>>;

function getRecommendations(answers: Answers): Pattern[] {
  const { interface: iface, challenge, complexity } = answers;

  let slugs: string[];

  if (challenge === 'sources') {
    slugs = ['source-attribution', 'confidence-disclosure', 'decision-explanation-on-demand'];
  } else if (challenge === 'memory') {
    slugs = ['visible-agent-memory', 'permission-scoping', 'agent-scope-definition'];
  } else if (iface === 'voice' && challenge === 'transparency') {
    slugs = ['designing-for-silence-voice-ui', 'plan-preview-and-live-progress'];
  } else if (iface === 'voice' && challenge === 'conversation') {
    slugs = ['no-screen-orientation-voice', 'clarification-without-interrogation'];
  } else if (iface === 'voice' && challenge === 'error') {
    slugs = ['graceful-agent-failure', 'graceful-topic-boundaries'];
  } else if (iface === 'agentic' && challenge === 'control' && complexity === 'high') {
    slugs = ['human-in-the-loop-checkpoint', 'agent-scope-definition', 'undo-agent-actions'];
  } else if (iface === 'agentic' && challenge === 'transparency') {
    slugs = ['autonomous-action-log', 'decision-explanation-on-demand', 'plan-preview-and-live-progress'];
  } else if (iface === 'agentic' && challenge === 'error') {
    slugs = ['graceful-agent-failure', 'undo-agent-actions'];
  } else if (iface === 'chat' && challenge === 'onboarding') {
    slugs = ['expectation-setting-for-ai-output', 'permission-scoping'];
  } else if (iface === 'chat' && challenge === 'conversation') {
    slugs = ['clarification-without-interrogation', 'graceful-topic-boundaries'];
  } else if (iface === 'chat' && challenge === 'transparency') {
    slugs = ['decision-explanation-on-demand', 'plan-preview-and-live-progress'];
  } else if (iface === 'chat' && challenge === 'control') {
    slugs = ['human-in-the-loop-checkpoint', 'undo-agent-actions'];
  } else if (iface === 'chat' && challenge === 'error') {
    slugs = ['graceful-agent-failure', 'clarification-without-interrogation'];
  } else {
    slugs = ['human-in-the-loop-checkpoint', 'expectation-setting-for-ai-output', 'graceful-agent-failure'];
  }

  return slugs
    .map(s => getPatternById(s))
    .filter((p): p is Pattern => p !== undefined);
}

export function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [phase, setPhase] = useState<'quiz' | 'results'>('quiz');
  const [animKey, setAnimKey] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  useReveal([phase]);

  const currentQ = QUESTIONS[step];
  const selectedTag = answers[currentQ?.key];

  function handleSelect(tag: string) {
    setAnswers(prev => ({ ...prev, [currentQ.key]: tag }));
  }

  function handleNext() {
    if (!selectedTag) return;
    if (step === QUESTIONS.length - 1) {
      setPhase('results');
    } else {
      setDirection('forward');
      setAnimKey(k => k + 1);
      setStep(s => s + 1);
    }
  }

  function handleBack() {
    if (step === 0) return;
    setDirection('back');
    setAnimKey(k => k + 1);
    setStep(s => s - 1);
  }

  function handleReset() {
    setAnswers({});
    setStep(0);
    setPhase('quiz');
    setDirection('forward');
    setAnimKey(k => k + 1);
  }

  if (phase === 'results') {
    const recommended = getRecommendations(answers);
    return (
      <main className={styles.page}>
        <div className={styles.resultsContainer}>
          <div className={styles.resultsHeader}>
            <p className={styles.resultsEyebrow}>Quiz results</p>
            <h1 className={`${styles.resultsTitle} rise`}>
              Based on your answers, these patterns are most relevant:
            </h1>
            <div className={styles.answerSummary}>
              {QUESTIONS.map(q => {
                const tag = answers[q.key];
                const opt = q.options.find(o => o.tag === tag);
                if (!opt) return null;
                return (
                  <div key={q.key} className={styles.answerChip}>
                    <span className={styles.answerLabel}>{q.shortLabel}</span>
                    <span className={styles.answerDivider}>·</span>
                    <span className={styles.answerValue}>{opt.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.resultsGrid}>
            {recommended.map((p, i) => (
              <PatternCard key={p.id} pattern={p} index={i} />
            ))}
          </div>

          <div className={styles.resultsActions}>
            <button className={styles.resetBtn} onClick={handleReset}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Start over
            </button>
            <Link to="/" className={styles.browseLink}>
              Browse all patterns
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <div className={styles.quizContainer}>
        <div
          key={animKey}
          className={`${styles.questionWrap} ${direction === 'forward' ? styles.slideForward : styles.slideBack}`}
        >
          <div className={styles.progress}>
            <span className={styles.progressText}>Question {step + 1} of {QUESTIONS.length}</span>
            <div className={styles.progressBar}>
              {QUESTIONS.map((_, i) => (
                <div
                  key={i}
                  className={`${styles.progressSegment} ${i <= step ? styles.progressFilled : ''}`}
                />
              ))}
            </div>
          </div>

          <h1 className={styles.questionText}>{currentQ.question}</h1>

          <div className={styles.options}>
            {currentQ.options.map(opt => (
              <button
                key={opt.tag}
                className={`${styles.option} ${selectedTag === opt.tag ? styles.optionSelected : ''}`}
                onClick={() => handleSelect(opt.tag)}
              >
                <span className={styles.optionRadio}>
                  {selectedTag === opt.tag && (
                    <svg width="8" height="8" viewBox="0 0 8 8">
                      <circle cx="4" cy="4" r="4" fill="currentColor" />
                    </svg>
                  )}
                </span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>

          <div className={styles.actions}>
            <div>
              {step > 0 && (
                <button className={styles.backBtn} onClick={handleBack}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
              )}
            </div>
            <div>
              {selectedTag && (
                <button className={styles.nextBtn} onClick={handleNext}>
                  {step === QUESTIONS.length - 1 ? 'See results' : 'Next'}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
